import { useState } from "react";
import SkorProtectBadge from "../SkorProtectBadge/SkorProtectBadge";
import { Button, ChoiceGroup, Modal } from "../design";
import GuarantorInviteSection, {
  GuarantorInvite,
} from "../GuarantorInviteSection/GuarantorInviteSection";
import {
  canJoinAsGuarantor,
  getInitials,
  resolveParticipantName,
  sortParticipantsWithGuarantors,
  toIdString,
} from "../../helper/applicationHelpers";
import styles from "./JoinApplicationScreen.module.css";

export type JoinAs = "flatmate" | "guarantor";

type JoinApplicationScreenProps = {
  app: any;
  /** joinAs is only sent from the Accept choice (decision mode) */
  onAccept: (guarantorInvites: GuarantorInvite[], joinAs?: JoinAs) => void;
  onDecline?: (guarantorInvites: GuarantorInvite[]) => void;
  onBack: () => void;
  processing: boolean;
  user: any;
  _t: (
    key: string,
    fallback?: string,
    // interpolation values ({{name}}); useTranslator takes them here
    args?: any
  ) => string;
  sharedLandlordNames?: string[];
};

const JoinApplicationScreen = ({
  app,
  onAccept,
  onDecline,
  onBack,
  processing,
  user,
  _t,
  sharedLandlordNames = [],
}: JoinApplicationScreenProps) => {
  const [guarantorInvites, setGuarantorInvites] = useState<GuarantorInvite[]>(
    []
  );
  // Accept first asks how to join: as a flatmate or as the inviter's guarantor
  // (only when they can be a guarantor in this application)
  const [joinChoiceOpen, setJoinChoiceOpen] = useState(false);
  const [joinAs, setJoinAs] = useState<JoinAs>("flatmate");
  const creator = app?.created_by;
  const creatorName =
    creator && typeof creator !== "string"
      ? `${creator.fname || creator.firstname || ""} ${creator.lname || creator.lastname || ""}`.trim() ||
      creator.email ||
      ""
      : "";
  const creatorEmail =
    creator && typeof creator !== "string" ? creator.email : null;
  const userEmail = user?.email?.toLowerCase();
  const ordered = [...(app?.participants ?? [])]
    .filter((participant: any) => !participant?.is_deleted)
    .sort((a, b) => {
      const rank = (participant: any) => {
        const email = participant?.email?.toLowerCase();
        if (creatorEmail && email === creatorEmail.toLowerCase()) return 0;
        if (userEmail && email === userEmail) return 1;
        return 2;
      };
      return rank(a) - rank(b);
    });
  // each guarantor right after the person they back
  const participants: any[] = sortParticipantsWithGuarantors(ordered);
  // guarantors are indented under the person they back, when listed
  const participantIds = new Set(
    participants.map((participant: any) => toIdString(participant?.id))
  );
  const isIndentedGuarantor = (participant: any) =>
    !!participant?.is_guarantor &&
    !!toIdString(participant?.guarantor_of) &&
    participantIds.has(toIdString(participant?.guarantor_of));

  const isDecisionMode = typeof onDecline === "function";
  const appTitle = app?.title || `${creatorName} ${_t("group")}`;
  const inviteTitle = isDecisionMode
    ? `${creatorName} ${_t("invitedYouTo", "invited you to")} ${appTitle}`
    : `${_t("youArePartOf")} ${appTitle}`;

  return (
    <main className={styles.card}>
      <Button
        className={styles.backButton}
        variant="ghost"
        size="sm"
        onClick={onBack}
        disabled={processing}
      >
        <span aria-hidden="true">‹</span> {_t("back")}
      </Button>

      <div className={styles.headingBlock}>
        <div className={styles.titleRow}>
          <h1>{inviteTitle}</h1>
          {app?.skor_protect && (
            <span className={styles.protectBadge}>
              <SkorProtectBadge />
            </span>
          )}
        </div>
        {sharedLandlordNames.length > 0 && (
          <div className={styles.sharedChips} aria-label="already shared">
            {sharedLandlordNames.map((name) => (
              <span key={name} className={styles.sharedChip}>
                {_t("alreadySharedWith", "Already shared with")} {name}
              </span>
            ))}
          </div>
        )}
        <p>
          {creatorName} {_t("invitedYouToThisApplication")}{" "}
          {!isDecisionMode && _t("yourVerifiedProfileHasBeenAddedToIt")}
        </p>
      </div>

      <section className={styles.people} aria-label={_t("applicationMembers")}>
        {participants.map((participant: any, index: number) => {
          const email = participant?.email || "";
          const isOwner =
            !!creatorEmail &&
            email.toLowerCase() === creatorEmail.toLowerCase();
          const isMe = !!userEmail && email.toLowerCase() === userEmail;
          const name = isMe ? _t("you") : resolveParticipantName(participant);

          return (
            <div
              className={[
                styles.personRow,
                isIndentedGuarantor(participant)
                  ? styles.personRowGuarantorOf
                  : "",
                // dividers only between tenants: none above their guarantors
                isIndentedGuarantor(participants[index + 1])
                  ? styles.personRowNoDivider
                  : "",
              ].join(" ")}
              key={email || index}
            >
              <span className={styles.avatar}>{getInitials(name)}</span>
              <div className={styles.personDetails}>
                <div className={styles.personName}>
                  <strong>{name}</strong>
                  <span>
                    {participant?.is_guarantor
                      ? _t("guarantor", "Guarantor")
                      : isOwner
                        ? _t("leadTenant")
                        : _t("tenant")}
                  </span>
                </div>
                <p>{email}</p>
              </div>
            </div>
          );
        })}

        <GuarantorInviteSection
          currentUserEmail={user?.email}
          blockedEmails={participants
            .map((participant: any) => participant?.email)
            .filter(Boolean)}
          invites={guarantorInvites}
          onChange={setGuarantorInvites}
          _t={_t}
          variant="join"
        />
      </section>

      <footer
        className={`${styles.footer} ${isDecisionMode ? styles.footerDecision : ""}`}
      >
        {isDecisionMode ? (
          <>
            <div className={styles.decisionButtons}>
              <Button
                variant="outline"
                size="lg"
                onClick={() => onDecline?.(guarantorInvites)}
                disabled={processing}
              >
                {_t("decline", "Decline")}
              </Button>
              <Button
                variant="primary"
                size="lg"
                onClick={() => {
                  // no choice when they can't be a guarantor here: they join
                  // with the role they were invited with (flatmate by default)
                  if (!canJoinAsGuarantor(app, user)) {
                    onAccept(guarantorInvites);
                    return;
                  }
                  setJoinAs("flatmate");
                  setJoinChoiceOpen(true);
                }}
                loading={processing}
              >
                {_t("acceptAndJoin", "Accept and join")}
              </Button>
            </div>
            <p className={styles.manageLater}>
              {_t(
                "manageChangesLaterApplicationsTab",
                "You can manage changes later from the Applications tab in your profile."
              )}
            </p>
          </>
        ) : (
          <>
            <p>{_t("manageGuarantorsAnytimeFromYourProfile")}</p>
            <Button
              variant="primary"
              size="lg"
              onClick={() => onAccept(guarantorInvites)}
              disabled={processing}
            >
              {processing ? "..." : _t("finish")}
            </Button>
          </>
        )}
      </footer>

      <Modal
        open={joinChoiceOpen}
        onClose={() => setJoinChoiceOpen(false)}
        size="sm"
        title={_t("howDoYouWantToJoin", "How do you want to join?")}
        closeLabel={_t("close")}
        actions={
          <>
            <Button
              variant="secondary"
              size="lg"
              onClick={() => setJoinChoiceOpen(false)}
            >
              {_t("cancel")}
            </Button>
            <Button
              variant="primary"
              size="lg"
              onClick={() => {
                setJoinChoiceOpen(false);
                onAccept(guarantorInvites, joinAs);
              }}
            >
              {_t("continue")}
            </Button>
          </>
        }
      >
        <ChoiceGroup
          name="join-as"
          layout="list"
          value={joinAs}
          onChange={(event) => setJoinAs(event.target.value as JoinAs)}
          options={[
            {
              value: "flatmate",
              label: _t("joinAsFlatmate", "Join as flatmate"),
              description: _t(
                "joinAsFlatmateText",
                "You will live in the apartment and share the application."
              ),
            },
            {
              value: "guarantor",
              label: _t("joinAsGuarantor", "Join as guarantor"),
              // a guarantor cannot bring their own guarantors
              disabled: guarantorInvites.length > 0,
              description:
                guarantorInvites.length > 0
                  ? _t(
                    "joinAsGuarantorRemoveGuarantors",
                    "Remove the guarantors you added to join as a guarantor."
                  )
                  : _t(
                    "joinAsGuarantorText",
                    "You will guarantee {{name}}'s rent and not live in the apartment.",
                    { name: creatorName }
                  ),
            },
          ]}
        />
      </Modal>
    </main>
  );
};

export default JoinApplicationScreen;
